import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { answerPackingAudit } from '../../../../services'

const QUICK_VALUES = [1, 2, 3, 4, 5]
const MIN_QUANTITY = 1
const MAX_QUANTITY = 99

const baseChoiceClasses =
  'h-9 min-w-[2.25rem] px-3 rounded-md border text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500'
const selectedChoiceClasses = 'border-indigo-600 bg-indigo-600 text-white'
const unselectedChoiceClasses = 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'

const YesNoToggle = ({ value, onChange }) => {
  const { t } = useTranslation()
  return (
    <div className="mt-2 flex gap-2">
      <button
        type="button"
        aria-pressed={value === true}
        onClick={() => onChange(true)}
        className={`${baseChoiceClasses} ${value === true ? selectedChoiceClasses : unselectedChoiceClasses}`}
      >
        {t('packing_audit.questions.yes')}
      </button>
      <button
        type="button"
        aria-pressed={value === false}
        onClick={() => onChange(false)}
        className={`${baseChoiceClasses} ${value === false ? selectedChoiceClasses : unselectedChoiceClasses}`}
      >
        {t('packing_audit.questions.no')}
      </button>
    </div>
  )
}

// Quantity selector: presets 1-5 plus a "+" toggle that reveals a free 1-99 input.
const QuantitySelector = ({ value, onChange }) => {
  const [customMode, setCustomMode] = useState(value != null && !QUICK_VALUES.includes(value))

  const selectPreset = (n) => {
    setCustomMode(false)
    onChange(n)
  }

  const handleCustomChange = (event) => {
    const raw = parseInt(event.target.value, 10)
    if (Number.isNaN(raw)) {
      onChange(null)
      return
    }
    onChange(Math.min(MAX_QUANTITY, Math.max(MIN_QUANTITY, raw)))
  }

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      {QUICK_VALUES.map((n) => {
        const isSelected = !customMode && value === n
        return (
          <button
            key={n}
            type="button"
            aria-pressed={isSelected}
            onClick={() => selectPreset(n)}
            className={`${baseChoiceClasses} ${isSelected ? selectedChoiceClasses : unselectedChoiceClasses}`}
          >
            {n}
          </button>
        )
      })}
      <button
        type="button"
        aria-pressed={customMode}
        onClick={() => setCustomMode(true)}
        className={`${baseChoiceClasses} ${customMode ? selectedChoiceClasses : unselectedChoiceClasses}`}
        aria-label="+"
      >
        +
      </button>
      {customMode && (
        <input
          type="number"
          min={MIN_QUANTITY}
          max={MAX_QUANTITY}
          value={value ?? ''}
          onChange={handleCustomChange}
          className="h-9 w-20 rounded-md border border-gray-300 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500"
        />
      )}
    </div>
  )
}

// Inline packing-audit questions block (§8.4 / §8.5). Rendered below the product
// list once packing is complete. On a successful answer it calls onAnswered();
// the parent then hides this block and shows the confirmation row.
export const PackingAuditQuestions = ({ orderId, scansCount, onAnswered }) => {
  const { t } = useTranslation()

  const [missingItems, setMissingItems] = useState(null)
  const [missingItemsQuantity, setMissingItemsQuantity] = useState(null)
  const [extraItems, setExtraItems] = useState(null)
  const [extraItemsQuantity, setExtraItemsQuantity] = useState(null)
  const [debrisPresent, setDebrisPresent] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(false)

  const handleMissingItemsChange = (value) => {
    setMissingItems(value)
    if (!value) setMissingItemsQuantity(null)
  }

  const handleExtraItemsChange = (value) => {
    setExtraItems(value)
    if (!value) setExtraItemsQuantity(null)
  }

  const isValid = useMemo(() => {
    if (missingItems === null || extraItems === null || debrisPresent === null) return false
    if (missingItems === true && !(missingItemsQuantity >= MIN_QUANTITY)) return false
    if (extraItems === true && !(extraItemsQuantity >= MIN_QUANTITY)) return false
    return true
  }, [missingItems, missingItemsQuantity, extraItems, extraItemsQuantity, debrisPresent])

  const handleSubmit = async () => {
    if (!isValid || submitting) return
    setSubmitting(true)
    setSubmitError(false)

    const payload = {
      missing_items: missingItems,
      extra_items: extraItems,
      debris_present: debrisPresent,
      scans_count: scansCount,
      ...(missingItems ? { missing_items_quantity: missingItemsQuantity } : {}),
      ...(extraItems ? { extra_items_quantity: extraItemsQuantity } : {}),
    }

    try {
      await answerPackingAudit(orderId, payload)
      onAnswered()
    } catch (e) {
      setSubmitError(true)
      setSubmitting(false)
    }
  }

  return (
    <section className="bg-white rounded-lg border-2 border-red-200 p-4">
      <div className="space-y-5">
        <div>
          <p className="text-base font-medium text-gray-900">
            {t('packing_audit.questions.missing_items.title')}
          </p>
          <YesNoToggle value={missingItems} onChange={handleMissingItemsChange} />
          {missingItems === true && (
            <>
              <p className="mt-3 text-sm text-gray-600">
                {t('packing_audit.questions.missing_items.quantity_prompt')}
              </p>
              <QuantitySelector value={missingItemsQuantity} onChange={setMissingItemsQuantity} />
            </>
          )}
        </div>

        <div>
          <p className="text-base font-medium text-gray-900">
            {t('packing_audit.questions.extra_items.title')}
          </p>
          <YesNoToggle value={extraItems} onChange={handleExtraItemsChange} />
          {extraItems === true && (
            <>
              <p className="mt-3 text-sm text-gray-600">
                {t('packing_audit.questions.extra_items.quantity_prompt')}
              </p>
              <QuantitySelector value={extraItemsQuantity} onChange={setExtraItemsQuantity} />
            </>
          )}
        </div>

        <div>
          <p className="text-base font-medium text-gray-900">
            {t('packing_audit.questions.debris_present.title')}
          </p>
          <YesNoToggle value={debrisPresent} onChange={setDebrisPresent} />
        </div>
      </div>

      <div className="mt-6">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!isValid || submitting}
          className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed sm:text-sm"
        >
          {submitting ? t('common.loading') : t('common.accept')}
        </button>
        {submitError && (
          <p className="mt-2 text-sm text-red-600">{t('packing_audit.questions.submit_error')}</p>
        )}
      </div>
    </section>
  )
}
